'use client';
import React, { useState } from 'react';
import BackgroundFX from '@/components/BackgroundFX';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import TopBar from '@/components/TopBar';
import Footer from '@/components/Footer';
import StatusBar from '@/components/StatusBar';
import SupportButton from '@/components/SupportButton';
import Image from 'next/image';

const PUNK_IMAGES = [
  '/assets/images/onchain_nodepunks_5720-1789380397040.png',
  '/assets/images/onchain_nodepunks_5692-1789380462925.png',
  '/assets/images/onchain_nodepunks_5718-1789380463455.png',
  '/assets/images/onchain_nodepunks_5680-1789380463891.png',
  '/assets/images/onchain_nodepunks_5631-1789380462383.png',
];

// Deterministic mock minted items for a given address
function getMintedForAddress(addr: string) {
  if (!addr || addr.length < 8) return [];
  const seed = addr.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const count = (seed % 5) + 1;
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    tokenId: ((seed * 31 + i * 97) % 3333) + 1,
    punkIndex: (seed + i) % PUNK_IMAGES.length,
  }));
}

export default function MyPage() {
  const [inputAddr, setInputAddr] = useState('');
  const [submittedAddr, setSubmittedAddr] = useState('');
  const [addrError, setAddrError] = useState('');

  const handleLookup = () => {
    const trimmed = inputAddr.trim();
    if (!trimmed) {
      setAddrError('Please enter your shielded Zcash address.');
      return;
    }
    if (!trimmed.startsWith('u1') && !trimmed.startsWith('zs')) {
      setAddrError('Address must start with u1 (unified) or zs (sapling).');
      return;
    }
    setAddrError('');
    setSubmittedAddr(trimmed);
  };

  const mintedItems = submittedAddr ? getMintedForAddress(submittedAddr) : [];

  return (
    <>
      <BackgroundFX />
      <div className="shell">
        <MobileNav />
        <Sidebar />
        <div className="main">
          <TopBar />
          <main style={{ padding: 'var(--main-top) 0 90px' }}>
            <div style={{
              maxWidth: 'var(--measure)', margin: '0 auto', width: '100%',
              padding: 'clamp(14px,2vw,20px) clamp(14px,2.5vw,24px) clamp(20px,3vw,32px)',
              animation: 'fadeIn var(--dur-3) var(--ease-out)',
            }}>
              <div style={{ marginBottom: 32 }}>
                <h1 style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 'clamp(26px,3vw,38px)', letterSpacing: '-0.03em', margin: '0 0 8px' }}>
                  My Profile
                </h1>
                <div style={{ fontSize: 13, color: 'var(--text-faint)' }}>
                  You just need your shielded address — no wallet required.
                </div>
              </div>

              {/* Shielded address input */}
              <div className="panel" style={{ padding: '28px 28px', maxWidth: 520, marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f4b728" strokeWidth="2" aria-hidden="true">
                    <path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" />
                  </svg>
                  <span style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 16 }}>
                    Enter Your Shielded Address
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-faint)', lineHeight: 1.6, marginBottom: 20 }}>
                  Enter your Zcash shielded address to view all your minted Zec Punks. Your portfolio is private — only you can look it up.
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 8 }}>
                    YOUR SHIELDED ZCASH ADDRESS
                  </label>
                  <textarea
                    value={inputAddr}
                    onChange={e => { setInputAddr(e.target.value); setAddrError(''); }}
                    placeholder="u1... or zs1..."
                    rows={3}
                    style={{
                      width: '100%', resize: 'none',
                      background: 'var(--bg-3)',
                      border: addrError ? '1px solid #f87171' : '1px solid var(--line-2)',
                      borderRadius: 10, padding: '12px 14px',
                      fontFamily: 'var(--mono)', fontSize: 12,
                      color: 'var(--text)', outline: 'none',
                      lineHeight: 1.6, boxSizing: 'border-box',
                    }}
                  />
                  {addrError && (
                    <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 11, color: '#f87171' }}>{addrError}</div>
                  )}
                </div>
                <button
                  onClick={handleLookup}
                  style={{
                    width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 700,
                    borderRadius: 12, border: 'none', cursor: 'pointer',
                    background: 'var(--gold)', color: '#1a1003',
                    fontFamily: 'var(--display)',
                    boxShadow: '0 4px 16px rgba(244,183,40,.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.6-3.6" />
                  </svg>
                  View My Minted Punks
                </button>
              </div>

              {/* Minted items */}
              {submittedAddr && (
                <div>
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <h2 style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 20, margin: 0 }}>
                        Minted Zec Punks
                      </h2>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-label="Verified">
                        <circle cx="12" cy="12" r="11" fill="#f4b728" />
                        <path d="M7.5 12.5l3 3 6-6.5" stroke="#1a1003" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </svg>
                    </div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'rgba(228,228,229,.4)', wordBreak: 'break-all' }}>
                      {submittedAddr.slice(0, 16)}…{submittedAddr.slice(-10)}
                    </div>
                  </div>

                  {mintedItems.length === 0 ? (
                    <div className="panel" style={{ padding: 32, textAlign: 'center', maxWidth: 400 }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
                      <div style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 16, marginBottom: 8 }}>No minted items found</div>
                      <div style={{ fontSize: 13, color: 'var(--text-faint)', lineHeight: 1.6 }}>
                        No Zec Punks found for this address yet. Mint some on the Collections page!
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 14 }}>
                      {mintedItems.map(item => (
                        <div
                          key={item.id}
                          className="panel"
                          style={{ padding: 0, overflow: 'hidden', borderRadius: 14 }}
                        >
                          <div style={{ position: 'relative', aspectRatio: '1/1', background: 'var(--bg-3)' }}>
                            <Image
                              src={PUNK_IMAGES[item.punkIndex]}
                              alt={`Zec Punks #${item.tokenId}`}
                              fill
                              style={{ objectFit: 'cover', imageRendering: 'pixelated' }}
                              sizes="200px"
                            />
                            <div style={{
                              position: 'absolute', top: 8, right: 8,
                              background: 'rgba(10,10,11,.75)',
                              borderRadius: 6, padding: '3px 7px',
                              display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="11" fill="#f4b728" />
                                <path d="M7.5 12.5l3 3 6-6.5" stroke="#1a1003" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                              </svg>
                              <span style={{ fontFamily: 'var(--mono)', fontSize: 8, color: '#f4b728', letterSpacing: '0.5px' }}>MINTED</span>
                            </div>
                          </div>
                          <div style={{ padding: '10px 12px' }}>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600, color: 'var(--gold)' }}>
                              Zec Punks #{item.tokenId}
                            </div>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(228,228,229,.4)', marginTop: 3 }}>
                              ⛓ On-chain · Shielded
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </main>
          <Footer />
        </div>
      </div>
      <StatusBar />
      <SupportButton />
    </>
  );
}
